<template>
  <div class="warranty-service-view">
    <!-- Toast powiadomienie -->
    <div v-if="showToast" class="success-toast">
      ✓ {{ toastMessage }}
    </div>

    <!-- Header -->
    <div class="page-header">
      <div class="header-content">
        <h1 class="page-title">🛠️ Serwis</h1>
        <p class="page-subtitle">Zarządzaj przypomnieniami o serwisie dla klientów</p>
      </div>
      <router-link to="/" class="btn-back">
        ← Powrót
      </router-link>
    </div>

    <div class="warranty-container">
      <!-- Statystyki -->
      <div class="stats-section">
        <div class="stat-card">
          <div class="stat-icon">⏳</div>
          <div class="stat-content">
            <div class="stat-value">{{ warrantyStats.pending }}</div>
            <div class="stat-label">Oczekujące na wysłanie</div>
            <p class="stat-description">instalacji czeka na przypomnienie o serwisie</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon">✅</div>
          <div class="stat-content">
            <div class="stat-value">{{ warrantyStats.sent }}</div>
            <div class="stat-label">Już wysłane</div>
            <p class="stat-description">przypomnień została wysłanych</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon">📊</div>
          <div class="stat-content">
            <div class="stat-value">{{ warrantyStats.total }}</div>
            <div class="stat-label">Razem Instalacji</div>
            <p class="stat-description">z aktywnym serwisem gwarancyjnym</p>
          </div>
        </div>
      </div>

      <!-- Sekcja Akcji -->
      <div class="action-section">
        <div class="action-card full-width">
          <div class="action-header">
            <h2 class="action-title">📧 Wysyłanie Przypomnień</h2>
            <p class="action-description">Wyślij emaily i/lub SMS-y do klientów</p>
          </div>

          <div class="action-content">
            <!-- Wybór kanału komunikacji -->
            <div class="communication-channels">
              <label class="channel-checkbox">
                <input type="checkbox" v-model="sendViaEmail" />
                <span class="channel-label">📧 Email</span>
              </label>
              <label class="channel-checkbox">
                <input type="checkbox" v-model="sendViaSMS" disabled/>
                <span class="channel-label">📱 SMS</span>
              </label>
            </div>

            <div class="send-modes">
              <button
                @click="sendWarrantyReminders()"
                class="btn-send-warranty btn-send-all"
                :disabled="isSendingWarranty"
              >
                <span class="btn-icon" v-if="!isSendingWarranty">📧</span>
                <span class="btn-icon spinner" v-if="isSendingWarranty">⏳</span>
                {{ isSendingWarranty ? 'Wysyłanie...' : 'Wyślij do wszystkich' }}
              </button>

              <button
                @click="sendWarrantyRemindersSelected()"
                class="btn-send-warranty btn-send-selected"
                :disabled="isSendingWarranty || selectedInstallations.length === 0"
              >
                <span class="btn-icon" v-if="!isSendingWarranty">✉️</span>
                <span class="btn-icon spinner" v-if="isSendingWarranty">⏳</span>
                {{ isSendingWarranty ? 'Wysyłanie...' : `Wyślij do zaznaczonych (${selectedInstallations.length})` }}
              </button>
            </div>

            <div v-if="warrantySendResult" class="warranty-result">
              <div v-if="warrantySendResult.success" class="result-success">
                <div class="result-header">
                  <span class="result-icon">✓</span>
                  <h3>Wysyłanie zakończone pomyślnie</h3>
                </div>
                <div class="result-details">
                  <p v-if="warrantySendResult.emailSent > 0"><strong>Wysłanych maili:</strong> {{ warrantySendResult.emailSent }}</p>
                  <p v-if="warrantySendResult.smsSent > 0"><strong>Wysłanych SMS:</strong> {{ warrantySendResult.smsSent }}</p>
                  <p v-if="warrantySendResult.errorCount > 0"><strong>Błędów:</strong> {{ warrantySendResult.errorCount }}</p>
                </div>
              </div>
              <div v-else class="result-error">
                <div class="result-header">
                  <span class="result-icon">✗</span>
                  <h3>Błąd podczas wysyłania</h3>
                </div>
                <p class="error-message">{{ warrantySendResult.error }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Jedna wspólna tabela Serwisów -->
        <div class="action-card full-width">
          <div class="action-header">
            <h2 class="action-title">📋 Przypomnienia o Serwisie (±30 dni)</h2>
            <p class="action-description">Instalacje wymagające serwisu w okresie 30 dni wstecz i 30 dni naprzód od dzisiaj</p>
          </div>

          <!-- Wyszukiwanie i filtrowanie -->
          <div class="search-section">
            <input 
              v-model="searchQuery"
              type="text"
              placeholder="🔍 Szukaj po kontrahentcie, adresie, segregatorze..."
              class="search-input"
            />
            <span v-if="searchQuery" @click="searchQuery = ''" class="search-clear">✕</span>
          </div>

          <div class="installations-list">
            <div v-if="filteredAndSortedReminders.length === 0" class="no-installations">
              <p v-if="serviceReminders.length === 0">Brak instalacji wymagających serwisu w tym okresie</p>
              <p v-else>Brak wyników dla wyszukiwania "{{ searchQuery }}"</p>
            </div>

            <div v-else class="installations-table unified-service-table">
              <div class="table-header">
                <div class="th-checkbox">
                  <input 
                    type="checkbox" 
                    @change="toggleSelectAll()"
                    :checked="selectedInstallations.length === filteredAndSortedReminders.length && filteredAndSortedReminders.length > 0"
                  />
                </div>
                <div class="th-contractor sortable" @click="toggleSort('contractor')">
                  Kontrahent (mail/tel)
                  <span class="sort-icon" v-if="sortColumn === 'contractor'">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                </div>
                <div class="th-address sortable" @click="toggleSort('address')">
                  Adres
                  <span class="sort-icon" v-if="sortColumn === 'address'">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                </div>
                <div class="th-binder sortable" @click="toggleSort('binder')">
                  Segregator
                  <span class="sort-icon" v-if="sortColumn === 'binder'">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                </div>
                <div class="th-installation-date sortable" @click="toggleSort('installationDate')">
                  Data montażu
                  <span class="sort-icon" v-if="sortColumn === 'installationDate'">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                </div>
                <div class="th-last-service sortable" @click="toggleSort('lastService')">
                  Ostatni serwis
                  <span class="sort-icon" v-if="sortColumn === 'lastService'">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                </div>
                <div class="th-planned-service sortable" @click="toggleSort('plannedService')">
                  Planowany serwis
                  <span class="sort-icon" v-if="sortColumn === 'plannedService'">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                </div>
                <div class="th-days">Dni</div>
                <div class="th-status">Status</div>
                <div class="th-nearby">Przy okazji</div>
                <div class="th-actions">Akcje</div>
              </div>

              <div 
                v-for="reminder in filteredAndSortedReminders" 
                :key="reminder.id"
                class="table-row"
                :class="getRowClass(reminder)"
                :style="getRowGroupStyle(reminder)"
              >
                <div class="td-checkbox" @click.stop>
                  <input 
                    type="checkbox" 
                    :value="reminder.id"
                    v-model="selectedInstallations"
                    :disabled="reminder.reminderSent"
                  />
                </div>
                <div class="td-contractor" @click="openInstallationModal(reminder.id)" style="cursor: pointer;">
                  <strong>{{ reminder.contractor.label || reminder.contractor.name }}</strong>
                  <small>{{ reminder.contractor.email }}</small>
                  <small v-if="reminder.contractor.phone">📱 {{ reminder.contractor.phone }}</small>
                </div>
                <div class="td-address" @click="openInstallationModal(reminder.id)" style="cursor: pointer;">
                  {{ reminder.address }}
                </div>
                <div class="td-binder" @click="openInstallationModal(reminder.id)" style="cursor: pointer; text-align: center;">
                  <b>{{ reminder.binder || '-' }}</b>
                </div>
                <div class="td-installation-date" @click="openInstallationModal(reminder.id)" style="cursor: pointer;">
                  {{ formatDate(reminder.installationDate) }}
                </div>
                <div class="td-last-service" @click="openInstallationModal(reminder.id)" style="cursor: pointer;">
                  {{ reminder.lastServiceDate ? formatDate(reminder.lastServiceDate) : '-' }}
                </div>
                <div class="td-planned-service" @click="openInstallationModal(reminder.id)" style="cursor: pointer;">
                  {{ reminder.plannedServiceDate ? formatDate(reminder.plannedServiceDate) : '-' }}
                </div>
                <div class="td-days">
                  <span class="days-badge" :class="getDaysBadgeClass(reminder.daysUntilExpiry)">
                    <template v-if="reminder.daysUntilExpiry < 0">-{{ Math.abs(reminder.daysUntilExpiry) }} dni</template>
                    <template v-else-if="reminder.dateCheckType === 'planned'">{{ reminder.daysUntilExpiry }} dni</template>
                    <template v-else>{{ reminder.daysUntilExpiry }} dni</template>
                  </span>
                </div>
                <div class="td-status">
                  <div v-if="reminder.underWarranty" class="status-warranty-wrapper">
                    <span class="status-badge status-warranty">✓ Na gwarancji</span>
                  </div>
                  <div v-else-if="reminder.reminderSent" class="status-sent-wrapper">
                    <span class="status-badge status-sent">Wysłano</span>
                  </div>
                  <span v-else class="status-badge status-pending">Oczekuje</span>
                </div>
                <div class="td-nearby" @click.stop>
                  <template v-if="(nearbyInstallationsMap.get(reminder.id) || []).length > 0">
                    <button
                      class="btn-nearby"
                      @click="toggleNearbyPopover(reminder.id)"
                      :title="`${(nearbyInstallationsMap.get(reminder.id) || []).length} inne instalacje kontrahenta`"
                    >
                      🔧 +{{ (nearbyInstallationsMap.get(reminder.id) || []).length }}
                    </button>
                    <div v-if="nearbyPopover.show && nearbyPopover.reminderId === reminder.id" class="nearby-popover">
                      <div class="nearby-popover-header">
                        <span>Inne instalacje kontrahenta</span>
                        <button @click.stop="closeNearbyPopover" class="nearby-popover-close">✕</button>
                      </div>
                      <div
                        v-for="item in nearbyPopover.items"
                        :key="item.id"
                        class="nearby-popover-item"
                        @click.stop="openInstallationModal(item.id); closeNearbyPopover()"
                      >
                        <div class="nearby-item-address">📍 {{ item.address }}</div>
                        <div class="nearby-item-meta">
                          <span v-if="item.binder !== '-'">📁 {{ item.binder }}</span>
                          <span v-if="item.plannedService">📅 {{ formatDate(item.plannedService) }}</span>
                          <span v-else-if="item.lastService">🔧 {{ formatDate(item.lastService) }}</span>
                        </div>
                      </div>
                    </div>
                  </template>
                  <span v-else class="nearby-none">—</span>
                </div>
                <div class="td-actions" @click.stop>
                  <button 
                    @click="openInstallationModal(reminder.id)"
                    class="btn-action-details"
                    title="Pokaż szczegóły"
                  >
                    ℹ️
                  </button>
                  <button 
                    @click="resendWarrantyReminder(reminder.id)"
                    class="btn-action"
                    title="Wyślij ponownie"
                  >
                    🔄
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Informacje -->
        <div class="info-section">
          <div class="info-card">
            <h3>ℹ️ Jak to działa?</h3>
            <ul class="info-list">
              <li>Kliknij przycisk aby wysłać email do <strong>wszystkich klientów</strong></li>
              <li>Emaily wysyłane są do kontrahentów z <strong>upłyniętą gwarancją</strong></li>
              <li>Każdy klient dostaje <strong>jeden email</strong> (bez duplikatów)</li>
              <li>Email zawiera: <strong>datę montażu, termin serwisu, kontakt</strong></li>
              <li>Wysłane przypomnienia są <strong>zapamiętywane</strong> w bazie danych</li>
            </ul>
          </div>

          <div class="info-card">
            <h3>📞 Dane w Emailu</h3>
            <ul class="info-list">
              <li>📅 Data instalacji (montażu)</li>
              <li>⏰ Termin serwisu</li>
              <li>👤 Imię i nazwisko kontrahenta</li>
              <li>📧 Email kontrahenta</li>
              <li>📱 Numer telefonu (z profilu)</li>
              <li>🏢 Logo i dane KLIM-BUD</li>
            </ul>
          </div>

          <div class="info-card">
            <h3>❓ FAQ</h3>
            <div class="faq-item">
              <p><strong>P: Czy mogę wysłać ponownie do tego samego klienta?</strong></p>
              <p>O: Tak, wyślij email do admina aby zresetować status w bazie danych</p>
            </div>
            <div class="faq-item">
              <p><strong>P: Co jeśli email nie został wysłany?</strong></p>
              <p>O: Sprawdź logi. Najczęściej problem jest z adresem email kontrahenta</p>
            </div>
            <div class="faq-item">
              <p><strong>P: Jak często mogę wysyłać?</strong></p>
              <p>O: Ile razy chcesz. Każdy klient otrzyma email tylko raz (jeśli nie zresetowano statusu)</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Installation Modal -->
    <installation-modal v-if="installation" :installation="installation" @update-installation="onInstallationUpdated"></installation-modal>
  </div>
</template>

<script>
import { db } from '../firebase';
import InstallationModal from '../components/InstallationModal';

export default {
  name: 'WarrantyService',
  components: {
    'installation-modal': InstallationModal
  },
  data() {
    return {
      isSendingWarranty: false,
      warrantySendResult: null,
      warrantyStats: {
        pending: 0,
        sent: 0,
        total: 0
      },
      showToast: false,
      toastMessage: '',
      serviceReminders: [],
      selectedInstallations: [],
      installation: null,
      sendViaEmail: true,
      sendViaSMS: false,
      searchQuery: '',
      sortColumn: 'binder', // Domyślnie sortowanie po segregatorze
      sortDirection: 'asc', // 'asc' lub 'desc'
      warrantyInstallationsCache: [],
      unsubscribeWarrantyInstallations: null,
      nearbyPopover: { show: false, reminderId: null, items: [] }
    };
  },
  methods: {
    async getContractorData(installation, contractorCache) {
      const embeddedContractor = installation.contractor || {};
      const contractorId = embeddedContractor.id;

      // Jeśli wbudowane dane mają email, użyj ich bez dodatkowego odczytu.
      if (embeddedContractor.email) {
        return embeddedContractor;
      }

      if (!contractorId) {
        return embeddedContractor;
      }

      if (contractorCache.has(contractorId)) {
        return contractorCache.get(contractorId);
      }

      const contractorDoc = await db.collection('contractors').doc(contractorId).get();
      const contractor = contractorDoc.exists ? contractorDoc.data() : null;
      contractorCache.set(contractorId, contractor);
      return contractor;
    },
    subscribeWarrantyInstallations() {
      if (this.unsubscribeWarrantyInstallations) {
        this.unsubscribeWarrantyInstallations();
      }

      this.unsubscribeWarrantyInstallations = db.collection('installations').onSnapshot(
        (snapshot) => {
          this.warrantyInstallationsCache = snapshot.docs.map(doc => ({
            id: doc.id,
            data: doc.data()
          }));

          // Przelicz dane widoku z lokalnego cache zamiast kolejnych zapytań.
          this.loadWarrantyStats();
          this.loadServiceReminders();
        },
        (error) => {
          console.error('Error subscribing warranty installations:', error);
          this.showToastMessage('❌ Błąd ładowania danych serwisowych', 'error');
        }
      );
    },
    async sendWarrantyReminders() {
      if (!this.sendViaEmail && !this.sendViaSMS) {
        this.showToastMessage('⚠️ Wybierz przynajmniej jeden kanał komunikacji', 'warning');
        return;
      }

      this.isSendingWarranty = true;
      this.warrantySendResult = null;

      try {
        // Pobierz wszystkie instalacje z reminderDate <= dzisiaj i reminderSent = false
        const now = new Date();
        const installationsSnapshot = await db.collection('installations')
          .where('warrantyReminder.enabled', '==', true)
          .where('warrantyReminder.reminderSent', '==', false)
          .where('warrantyReminder.reminderDate', '<=', now)
          .get();

        if (installationsSnapshot.empty) {
          this.showToastMessage('⚠️ Brak instalacji do wysłania przypomnienia', 'warning');
          this.isSendingWarranty = false;
          return;
        }

        let emailSent = 0;
        let smsSent = 0;
        let errorCount = 0;
        const errors = [];
        const contractorCache = new Map();

        // Wysyłaj pojedynczo
        for (const doc of installationsSnapshot.docs) {
          const installation = doc.data();
          
          try {
            const contractor = await this.getContractorData(installation, contractorCache);

            if (!contractor) {
              errorCount++;
              errors.push(`Brak danych kontrahenta dla ${installation.address || 'instalacji'}`);
              continue;
            }

            const installationDateFormatted = this.formatDate(installation.date);
            const reminderDateValue = installation.warrantyReminder && installation.warrantyReminder.reminderDate
              ? installation.warrantyReminder.reminderDate
              : null;
            const serviceDateFormatted = this.formatDate(reminderDateValue);
            const safeInstallationDate = installationDateFormatted === '-' ? 'Brak danych' : installationDateFormatted;
            const safeServiceDate = serviceDateFormatted === '-' ? 'Brak danych' : serviceDateFormatted;
            const contractorAddress = contractor.address || installation.address || 'Adres nieokreślony';

            const sentMethods = [];

            // Wysyłanie EMAIL
            if (this.sendViaEmail) {
              if (!contractor.email) {
                errorCount++;
                errors.push(`Brak emaila dla ${contractorAddress}`);
              } else {
                try {
                  const emailResponse = await fetch('https://klim-bud.pl/wp-json/klimbud/v1/send-warranty-reminder', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                      email: contractor.email,
                      contractorName: contractor.label,
                      contractorAddress: contractorAddress,
                      installationAddress: contractorAddress,
                      installationDate: safeInstallationDate,
                      warrantyEndDate: safeServiceDate
                    })
                  });

                  if (emailResponse.ok) {
                    const emailData = await emailResponse.json();
                    if (emailData.success) {
                      emailSent++;
                      sentMethods.push('email');
                    } else {
                      errorCount++;
                      errors.push(`Email: ${emailData.error || 'Błąd'} - ${contractorAddress}`);
                    }
                  } else {
                    errorCount++;
                    errors.push(`Email: Błąd serwera - ${contractorAddress}`);
                  }
                } catch (err) {
                  errorCount++;
                  errors.push(`Email: ${err.message} - ${contractorAddress}`);
                }
              }
            }

            // Wysyłanie SMS - TYMCZASOWO WYŁĄCZONE
            /* if (this.sendViaSMS) {
              const phone = contractor.phone || contractor.phoneNumber;
              if (!phone) {
                errorCount++;
                errors.push(`Brak telefonu dla ${contractorAddress}`);
              } else {
                try {
                  const smsMessage = `KLIM-BUD: Przypominamy o serwisie instalacji z ${safeInstallationDate}. Termin: ${safeServiceDate}. Kontakt: 123-456-789`;
                  
                  const smsResponse = await fetch('https://klim-bud.pl/wp-json/klimbud/v1/send-sms', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                      phone: phone,
                      message: smsMessage
                    })
                  });

                  if (smsResponse.ok) {
                    const smsData = await smsResponse.json();
                    if (smsData.success) {
                      smsSent++;
                      sentMethods.push('sms');
                    } else {
                      errorCount++;
                      errors.push(`SMS: ${smsData.error || 'Błąd'} - ${contractorAddress}`);
                    }
                  } else {
                    errorCount++;
                    errors.push(`SMS: Błąd serwera - ${contractorAddress}`);
                  }
                } catch (err) {
                  errorCount++;
                  errors.push(`SMS: ${err.message} - ${contractorAddress}`);
                }
              }
            } */

            // Zaktualizuj status w Firestore jeśli wysłano przynajmniej jedną wiadomość
            if (sentMethods.length > 0) {
              await db.collection('installations').doc(doc.id).update({
                'warrantyReminder.reminderSent': true,
                'warrantyReminder.sentDate': new Date(),
                'warrantyReminder.sentMethod': sentMethods
              });
            }
          } catch (err) {
            console.error('Error sending warranty reminder:', err);
            errorCount++;
            errors.push(`Wyjątek: ${err.message}`);
          }
        }

        this.warrantySendResult = {
          success: emailSent > 0 || smsSent > 0,
          emailSent: emailSent,
          smsSent: smsSent,
          errorCount: errorCount,
          errors: errors
        };
        
        if (emailSent > 0 || smsSent > 0) {
          const parts = [];
          if (emailSent > 0) parts.push(`${emailSent} maili`);
          if (smsSent > 0) parts.push(`${smsSent} SMS-ów`);
          this.showToastMessage(`✓ Wysłano ${parts.join(' i ')}`);
        }
        
        // Załaduj statystyki
        setTimeout(() => {
          this.loadWarrantyStats();
          this.loadServiceReminders();
        }, 1000);
        
      } catch (error) {
        console.error('Error sending warranty reminders:', error);
        this.warrantySendResult = {
          success: false,
          error: 'Nie udało się wysłać przypomnień: ' + error.message
        };
        this.showToastMessage('❌ Błąd podczas wysyłania', 'error');
      } finally {
        this.isSendingWarranty = false;
      }
    },
    async sendWarrantyRemindersSelected() {
      if (this.selectedInstallations.length === 0) {
        alert('Wybierz przynajmniej jedną instalację');
        return;
      }

      if (!this.sendViaEmail && !this.sendViaSMS) {
        this.showToastMessage('⚠️ Wybierz przynajmniej jeden kanał komunikacji', 'warning');
        return;
      }

      this.isSendingWarranty = true;
      this.warrantySendResult = null;

      try {
        let emailSent = 0;
        let smsSent = 0;
        let errorCount = 0;
        const errors = [];
        const contractorCache = new Map();

        // Wysyłaj do wybranych instalacji
        for (const installationId of this.selectedInstallations) {
          try {
            // Pobierz dane instalacji
            const installationDoc = await db.collection('installations').doc(installationId).get();
            if (!installationDoc.exists) {
              errorCount++;
              errors.push(`Instalacja ${installationId} nie istnieje`);
              continue;
            }

            const installation = installationDoc.data();
            
            const contractor = await this.getContractorData(installation, contractorCache);

            if (!contractor) {
              errorCount++;
              errors.push(`Brak danych kontrahenta dla ${installation.address || 'instalacji'}`);
              continue;
            }

            const installationDateFormatted = this.formatDate(installation.date);
            const reminderDateValue = installation.warrantyReminder && installation.warrantyReminder.reminderDate
              ? installation.warrantyReminder.reminderDate
              : null;
            const serviceDateFormatted = this.formatDate(reminderDateValue);
            const safeInstallationDate = installationDateFormatted === '-' ? 'Brak danych' : installationDateFormatted;
            const safeServiceDate = serviceDateFormatted === '-' ? 'Brak danych' : serviceDateFormatted;
            const contractorAddress = contractor.address || installation.address || 'Adres nieokreślony';

            const sentMethods = [];

            // Wysyłanie EMAIL
            if (this.sendViaEmail) {
              if (!contractor.email) {
                errorCount++;
                errors.push(`Brak emaila dla ${contractorAddress}`);
              } else {
                try {
                  const emailResponse = await fetch('https://klim-bud.pl/wp-json/klimbud/v1/send-warranty-reminder', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                      email: contractor.email,
                      contractorName: contractor.label,
                      contractorAddress: contractorAddress,
                      installationAddress: contractorAddress,
                      installationDate: safeInstallationDate,
                      warrantyEndDate: safeServiceDate
                    })
                  });

                  if (emailResponse.ok) {
                    const emailData = await emailResponse.json();
                    if (emailData.success) {
                      emailSent++;
                      sentMethods.push('email');
                    } else {
                      errorCount++;
                      errors.push(`Email: ${emailData.error || 'Błąd'} - ${contractorAddress}`);
                    }
                  } else {
                    errorCount++;
                    errors.push(`Email: Błąd serwera - ${contractorAddress}`);
                  }
                } catch (err) {
                  errorCount++;
                  errors.push(`Email: ${err.message} - ${contractorAddress}`);
                }
              }
            }

            /* // Wysyłanie SMS
            if (this.sendViaSMS) {
              const phone = contractor.phone || contractor.phoneNumber;
              if (!phone) {
                errorCount++;
                errors.push(`Brak telefonu dla ${contractorAddress}`);
              } else {
                try {
                  const smsMessage = `KLIM-BUD: Przypominamy o serwisie instalacji z ${safeInstallationDate}. Termin: ${safeServiceDate}. Kontakt: +48 574 559 899`;
                  
                  const smsResponse = await fetch('https://klim-bud.pl/wp-json/klimbud/v1/send-sms', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                      phone: phone,
                      message: smsMessage
                    })
                  });

                  if (smsResponse.ok) {
                    const smsData = await smsResponse.json();
                    if (smsData.success) {
                      smsSent++;
                      sentMethods.push('sms');
                    } else {
                      errorCount++;
                      errors.push(`SMS: ${smsData.error || 'Błąd'} - ${contractorAddress}`);
                    }
                  } else {
                    errorCount++;
                    errors.push(`SMS: Błąd serwera - ${contractorAddress}`);
                  }
                } catch (err) {
                  errorCount++;
                  errors.push(`SMS: ${err.message} - ${contractorAddress}`);
                }
              }
            } */

            // Zaktualizuj status w Firestore jeśli wysłano przynajmniej jedną wiadomość
            if (sentMethods.length > 0) {
              await db.collection('installations').doc(installationId).update({
                'warrantyReminder.reminderSent': true,
                'warrantyReminder.sentDate': new Date(),
                'warrantyReminder.sentMethod': sentMethods
              });
            }
          } catch (err) {
            console.error('Error sending warranty reminder:', err);
            errorCount++;
            errors.push(`Wyjątek: ${err.message}`);
          }
        }

        this.warrantySendResult = {
          success: emailSent > 0 || smsSent > 0,
          emailSent: emailSent,
          smsSent: smsSent,
          errorCount: errorCount,
          errors: errors
        };
        
        if (emailSent > 0 || smsSent > 0) {
          const parts = [];
          if (emailSent > 0) parts.push(`${emailSent} maili`);
          if (smsSent > 0) parts.push(`${smsSent} SMS-ów`);
          this.showToastMessage(`✓ Wysłano ${parts.join(' i ')} do zaznaczonych`);
        }
        
        // Wyczyść zaznaczenia i załaduj dane ponownie
        this.selectedInstallations = [];
        setTimeout(() => {
          this.loadWarrantyStats();
          this.loadServiceReminders();
        }, 1000);
        
      } catch (error) {
        console.error('Error sending warranty reminders:', error);
        this.warrantySendResult = {
          success: false,
          error: 'Nie udało się wysłać przypomnień: ' + error.message
        };
        this.showToastMessage('❌ Błąd podczas wysyłania', 'error');
      } finally {
        this.isSendingWarranty = false;
      }
    },
    async loadServiceReminders() {
      try {
        const now = new Date();
        
        // Oblicz zakres: 30 dni wstecz i 30 dni do przodu (bez względu na rok)
        let startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 30);
        const startOfRange = { day: startDate.getDate(), month: startDate.getMonth() + 1 };

        let endDate = new Date(now);
        endDate.setDate(endDate.getDate() + 30);
        const endOfRange = { day: endDate.getDate(), month: endDate.getMonth() + 1 };
        
        const reminders = [];

        this.warrantyInstallationsCache.forEach(item => {
          const data = item.data;
          
          // Sprawdź datę montażu
          let installationDate;
          if (data.date) {
            if (typeof data.date.toDate === 'function') {
              installationDate = data.date.toDate();
            } else {
              installationDate = new Date(data.date);
            }
          }
          
          if (!installationDate || !(installationDate instanceof Date) || isNaN(installationDate.getTime())) {
            return;
          }
          
          // Sprawdź datę ostatniego serwisu
          let lastServiceDate = null;
          if (data.service) {
            if (typeof data.service.toDate === 'function') {
              lastServiceDate = data.service.toDate();
            } else if (data.service instanceof Date) {
              lastServiceDate = data.service;
            } else {
              lastServiceDate = new Date(data.service);
            }
            
            if (!(lastServiceDate instanceof Date) || isNaN(lastServiceDate.getTime())) {
              lastServiceDate = null;
            }
          }
          
          // Sprawdź datę planowanego serwisu
          let plannedServiceDate = null;
          if (data.plannedService) {
            if (typeof data.plannedService.toDate === 'function') {
              plannedServiceDate = data.plannedService.toDate();
            } else if (data.plannedService instanceof Date) {
              plannedServiceDate = data.plannedService;
            } else {
              plannedServiceDate = new Date(data.plannedService);
            }
            
            if (!(plannedServiceDate instanceof Date) || isNaN(plannedServiceDate.getTime())) {
              plannedServiceDate = null;
            }
          }
          
          // Priorytet dat do sprawdzenia (±30 dni bez względu na rok):
          // 1. NAJPIERW: Data planowanego serwisu (jeśli istnieje I jest na bieżący rok lub wcześniej)
          // 2. JEŚLI NIE MA lub jest na przyszły rok: Data ostatniego serwisu (jeśli istnieje)
          // 3. JEŚLI NIE MA: Data montażu (zawsze istnieje)
          
          const currentYear = now.getFullYear();
          
          // WARUNEK WYKLUCZAJĄCY: Jeśli planowany serwis jest na przyszły rok I ostatni serwis już był wykonany
          // to nie wyświetlaj tej instalacji w ogóle (serwis wykonany, następny daleko w przyszłości)
          if (plannedServiceDate && plannedServiceDate.getFullYear() > currentYear && lastServiceDate) {
            return; // Pomiń tę instalację
          }
          
          let dateToCheck = installationDate; // Domyślnie data montażu
          let dateCheckType = 'installation';
          
          if (plannedServiceDate && plannedServiceDate.getFullYear() <= currentYear) {
            // Jeśli jest ustawiona data planowanego serwisu I jest na bieżący rok (lub wcześniej) - ma najwyższy priorytet
            dateToCheck = plannedServiceDate;
            dateCheckType = 'planned';
          } else if (lastServiceDate) {
            // Jeśli nie ma planowanego na bieżący rok, ale jest ostatni serwis - użyj go
            dateToCheck = lastServiceDate;
            dateCheckType = 'last';
          }
          // Jeśli nie ma ani planowanego (na bieżący rok) ani ostatniego - zostaje data montażu
          
          // Sprawdź czy dzień/miesiąc wybranej daty jest w zakresie ±30 dni (bez względu na rok)
          // Bierzemy tylko dzień i miesiąc, ignorujemy rok
          const checkDay = dateToCheck.getDate();
          const checkMonth = dateToCheck.getMonth() + 1;
          
          let isInRange = false;
          
          if (startOfRange.month === endOfRange.month) {
            // Zakres w obrębie jednego miesiąca
            isInRange = checkDay >= startOfRange.day && checkDay <= endOfRange.day && checkMonth === startOfRange.month;
          } else if (startOfRange.month < endOfRange.month) {
            // Zakres w obrębie roku
            isInRange = (checkMonth === startOfRange.month && checkDay >= startOfRange.day) ||
                       (checkMonth === endOfRange.month && checkDay <= endOfRange.day) ||
                       (checkMonth > startOfRange.month && checkMonth < endOfRange.month);
          } else {
            // Zakres przechodzi przez koniec roku (np. grudzień-styczeń)
            isInRange = (checkMonth === startOfRange.month && checkDay >= startOfRange.day) ||
                       (checkMonth > startOfRange.month) ||
                       (checkMonth < endOfRange.month) ||
                       (checkMonth === endOfRange.month && checkDay <= endOfRange.day);
          }
          
          if (isInRange) {
            // Pobierz telefon kontrahenta
            let contractorPhone = '';
            if (data.contractor && data.contractor.phone) {
              contractorPhone = data.contractor.phone;
            } else if (data.contractor && data.contractor.phoneNumber) {
              contractorPhone = data.contractor.phoneNumber;
            }
            
            reminders.push({
              id: item.id,
              contractor: {
                ...data.contractor,
                phone: contractorPhone
              },
              address: (data.contractor && data.contractor.address) || data.address || '-',
              binder: data.binder || '-',
              underWarranty: data.underWarranty || false,
              installationDate: installationDate,
              lastServiceDate: lastServiceDate,
              plannedServiceDate: plannedServiceDate,
              dateToCheck: dateToCheck,
              dateCheckType: dateCheckType,
              daysUntilExpiry: this.calculateDaysUntilDate(dateToCheck, dateCheckType),
              reminderSent: data.serviceReminderSent || (data.warrantyReminder && data.warrantyReminder.reminderSent) || false
            });
          }
        });
        
        // Sortuj po dacie do sprawdzenia (dzień/miesiąc)
        reminders.sort((a, b) => {
          const aMonth = a.dateToCheck.getMonth();
          const aDay = a.dateToCheck.getDate();
          const bMonth = b.dateToCheck.getMonth();
          const bDay = b.dateToCheck.getDate();
          
          if (aMonth !== bMonth) return aMonth - bMonth;
          return aDay - bDay;
        });
        
        this.serviceReminders = reminders;
      } catch (error) {
        console.error('Error loading service reminders:', error);
        this.serviceReminders = [];
      }
    },
    toggleSelectAll() {
      if (this.selectedInstallations.length === this.serviceReminders.length) {
        this.selectedInstallations = [];
      } else {
        this.selectedInstallations = this.serviceReminders
          .filter(r => !r.reminderSent)
          .map(r => r.id);
      }
    },
    isSelected(id) {
      return this.selectedInstallations.includes(id);
    },
    toggleSort(column) {
      if (this.sortColumn === column) {
        // Jeśli klikniemy na już sortowaną kolumnę, zmień kierunek
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        // Jeśli nowa kolumna, ustaw sortowanie rosnące
        this.sortColumn = column;
        this.sortDirection = 'asc';
      }
    },
    formatDate(date) {
      if (!date) return '-';
      const normalizedDate = (date && typeof date.toDate === 'function') ? date.toDate() : date;
      const d = normalizedDate instanceof Date ? normalizedDate : new Date(normalizedDate);
      if (isNaN(d.getTime())) return '-';
      return d.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
    getDaysBadgeClass(days) {
      if (days < 0) return 'badge-expired';
      if (days <= 7) return 'badge-urgent';
      if (days <= 14) return 'badge-soon';
      return 'badge-normal';
    },
    calculateDaysUntilDate(date, dateType = 'planned') {
      if (!date || !(date instanceof Date) || isNaN(date.getTime())) return null;
      
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);

      // Dla ostatniego serwisu (last) - oblicz rzeczywiste dni od tamtej daty
      if (dateType === 'last') {
        // Ustaw godziny na 0 dla daty serwisu
        const serviceDate = new Date(date);
        serviceDate.setHours(0, 0, 0, 0);
        // Negative = przeszłość (dni temu), positive = przyszłość
        const diffDays = Math.ceil((serviceDate - todayStart) / (1000 * 60 * 60 * 24));
        return diffDays;
      }

      // Dla planowanego serwisu (planned) i montażu (installation) - oblicz do rocznicy
      const thisYearDate = new Date(todayStart.getFullYear(), date.getMonth(), date.getDate());
      let diffDays = Math.ceil((thisYearDate - todayStart) / (1000 * 60 * 60 * 24));

      // Jeśli różnica jest mniejsza niż -15 dni, to znaczy, że wypadliśmy w poprzednim roku
      // i powinniśmy liczyć do następnej rocznicy w kolejnym roku.
      if (diffDays < -15) {
        const nextYearDate = new Date(todayStart.getFullYear() + 1, date.getMonth(), date.getDate());
        diffDays = Math.ceil((nextYearDate - todayStart) / (1000 * 60 * 60 * 24));
      }

      return diffDays;
    },
    async loadWarrantyStats() {
      try {
        const now = new Date();

        // Oblicz zakres: 30 dni wstecz i 30 dni do przodu (bez względu na rok)
        let startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 30);
        const startOfRange = { day: startDate.getDate(), month: startDate.getMonth() + 1 };

        let endDate = new Date(now);
        endDate.setDate(endDate.getDate() + 30);
        const endOfRange = { day: endDate.getDate(), month: endDate.getMonth() + 1 };
        
        let pending = 0;
        let sent = 0;
        let total = 0;

        this.warrantyInstallationsCache.forEach(item => {
          const data = item.data;
          
          // Sprawdź datę montażu
          let installationDate;
          if (data.date && typeof data.date.toDate === 'function') {
            installationDate = data.date.toDate();
          } else if (data.date) {
            installationDate = new Date(data.date);
          }
          
          if (!installationDate || !(installationDate instanceof Date) || isNaN(installationDate.getTime())) return;
          
          // Sprawdź datę ostatniego serwisu
          let lastServiceDate = null;
          if (data.service) {
            if (typeof data.service.toDate === 'function') {
              lastServiceDate = data.service.toDate();
            } else if (data.service instanceof Date) {
              lastServiceDate = data.service;
            } else {
              lastServiceDate = new Date(data.service);
            }
            
            if (!(lastServiceDate instanceof Date) || isNaN(lastServiceDate.getTime())) {
              lastServiceDate = null;
            }
          }
          
          // Sprawdź datę planowanego serwisu
          let plannedServiceDate = null;
          if (data.plannedService) {
            if (typeof data.plannedService.toDate === 'function') {
              plannedServiceDate = data.plannedService.toDate();
            } else if (data.plannedService instanceof Date) {
              plannedServiceDate = data.plannedService;
            } else {
              plannedServiceDate = new Date(data.plannedService);
            }
            
            if (!(plannedServiceDate instanceof Date) || isNaN(plannedServiceDate.getTime())) {
              plannedServiceDate = null;
            }
          }
          
          // Priorytet dat do sprawdzenia (±30 dni bez względu na rok):
          // 1. NAJPIERW: Data planowanego serwisu (jeśli istnieje I jest na bieżący rok lub wcześniej)
          // 2. JEŚLI NIE MA lub jest na przyszły rok: Data ostatniego serwisu (jeśli istnieje)
          // 3. JEŚLI NIE MA: Data montażu (zawsze istnieje)
          
          const currentYear = now.getFullYear();
          
          // WARUNEK WYKLUCZAJĄCY: Jeśli planowany serwis jest na przyszły rok I ostatni serwis już był wykonany
          // to nie wyświetlaj tej instalacji w ogóle (serwis wykonany, następny daleko w przyszłości)
          if (plannedServiceDate && plannedServiceDate.getFullYear() > currentYear && lastServiceDate) {
            return; // Pomiń tę instalację
          }
          
          let dateToCheck = installationDate; // Domyślnie data montażu
          
          if (plannedServiceDate && plannedServiceDate.getFullYear() <= currentYear) {
            dateToCheck = plannedServiceDate;
          } else if (lastServiceDate) {
            dateToCheck = lastServiceDate;
          }
          
          const checkDay = dateToCheck.getDate();
          const checkMonth = dateToCheck.getMonth() + 1;
          
          let isInRange = false;
          if (startOfRange.month === endOfRange.month) {
            isInRange = checkDay >= startOfRange.day && checkDay <= endOfRange.day && checkMonth === startOfRange.month;
          } else if (startOfRange.month < endOfRange.month) {
            isInRange = (checkMonth === startOfRange.month && checkDay >= startOfRange.day) ||
                       (checkMonth === endOfRange.month && checkDay <= endOfRange.day) ||
                       (checkMonth > startOfRange.month && checkMonth < endOfRange.month);
          } else {
            isInRange = (checkMonth === startOfRange.month && checkDay >= startOfRange.day) ||
                       (checkMonth > startOfRange.month) ||
                       (checkMonth < endOfRange.month) ||
                       (checkMonth === endOfRange.month && checkDay <= endOfRange.day);
          }
          
          if (isInRange) {
            total++;
            
            const reminderSent = data.serviceReminderSent || (data.warrantyReminder && data.warrantyReminder.reminderSent);
            if (reminderSent) {
              sent++;
            } else {
              pending++;
            }
          }
        });
        
        this.warrantyStats = { pending, sent, total };
      } catch (error) {
        console.error('Error loading warranty stats:', error);
        this.warrantyStats = { pending: 0, sent: 0, total: 0 };
      }
    },
    showToastMessage(message) {
      this.toastMessage = message;
      this.showToast = true;
      setTimeout(() => {
        this.showToast = false;
      }, 4000);
    },
    toggleNearbyPopover(reminderId) {
      if (this.nearbyPopover.show && this.nearbyPopover.reminderId === reminderId) {
        this.nearbyPopover.show = false;
        this.nearbyPopover.reminderId = null;
        this.nearbyPopover.items = [];
      } else {
        this.nearbyPopover.show = true;
        this.nearbyPopover.reminderId = reminderId;
        this.nearbyPopover.items = this.nearbyInstallationsMap.get(reminderId) || [];
      }
    },
    closeNearbyPopover() {
      this.nearbyPopover.show = false;
      this.nearbyPopover.reminderId = null;
      this.nearbyPopover.items = [];
    },
    getRowClass(reminder) {
      const meta = this.groupMetaMap.get(reminder.id) || {};
      return {
        'selected': this.isSelected(reminder.id),
        'group-row': !!meta.grouped,
        'group-first': meta.position === 'first',
        'group-middle': meta.position === 'middle',
        'group-last': meta.position === 'last',
        'group-only': meta.position === 'only',
      };
    },
    getRowGroupStyle(reminder) {
      const meta = this.groupMetaMap.get(reminder.id);
      if (meta && meta.grouped) return { '--group-color': meta.color };
      return {};
    },
    onInstallationUpdated() {
      // Show success toast
      this.showToastMessage('✏️ Instalacja została uaktualniona');
      // Refresh data
      this.loadServiceReminders();
      this.loadWarrantyStats();
      // Close modal
      setTimeout(() => {
        this.installation = null;
        this.$modal.hide('installation-modal');
      }, 500);
    },
    openInstallationModal(installationId) {
      // Pobierz pełne dane instalacji
      db.collection('installations').doc(installationId).get().then(doc => {
        if (doc.exists) {
          const docData = doc.data();
          // Map 'notice' field to 'content' for InstallationModal compatibility
          const installationData = {
            ...docData,
            id: doc.id,
            content: docData.notice || ""
          };
          // Wrap to match InstallationModal expectations: { contractor, installation }
          this.installation = {
            contractor: docData.contractor,
            installation: installationData
          };
          this.$modal.show('installation-modal');
        }
      }).catch(err => {
        console.error('Error loading installation:', err);
        this.showToastMessage('❌ Błąd podczas ładowania instalacji', 'error');
      });
    },
    async resendWarrantyReminder(installationId) {
      if (!confirm('Czy na pewno chcesz wysłać przypomnienie ponownie?')) {
        return;
      }
      
      try {
        // Pobierz dane instalacji
        const installationDoc = await db.collection('installations').doc(installationId).get();
        if (!installationDoc.exists) {
          this.showToastMessage('❌ Instalacja nie istnieje', 'error');
          return;
        }

        const installation = installationDoc.data();
        
        const contractorCache = new Map();
        const contractor = await this.getContractorData(installation, contractorCache);

        if (!contractor || !contractor.email) {
          this.showToastMessage('❌ Brak emaila kontrahenta', 'error');
          return;
        }

        const installationDateFormatted = this.formatDate(installation.date);
        const reminderDateValue = installation.warrantyReminder && installation.warrantyReminder.reminderDate
          ? installation.warrantyReminder.reminderDate
          : null;
        const serviceDateFormatted = this.formatDate(reminderDateValue);
        const safeInstallationDate = installationDateFormatted === '-' ? 'Brak danych' : installationDateFormatted;
        const safeServiceDate = serviceDateFormatted === '-' ? 'Brak danych' : serviceDateFormatted;
        const contractorAddress = contractor.address || installation.address || 'Adres nieokreślony';

        // Wysyłanie przez WordPress REST API
        const response = await fetch('https://klim-bud.pl/wp-json/klimbud/v1/send-warranty-reminder', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: contractor.email,
            contractorName: contractor.label,
            contractorAddress: contractorAddress,
            installationAddress: contractorAddress,
            installationDate: safeInstallationDate,
            warrantyEndDate: safeServiceDate
          })
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('WordPress API error:', errorText);
          this.showToastMessage('❌ Błąd wysyłania maila', 'error');
          return;
        }

        const data = await response.json();

        if (data.success) {
          // Zaktualizuj datę wysłania
          await db.collection('installations').doc(installationId).update({
            'warrantyReminder.sentDate': new Date()
          });
          
          this.showToastMessage('✓ Mail wysłany ponownie');
          
          // Odśwież dane
          setTimeout(() => {
            this.loadServiceReminders();
          }, 1000);
        } else {
          this.showToastMessage('❌ ' + (data.error || 'Błąd wysyłania'), 'error');
        }
      } catch (err) {
        console.error('Error resending warranty email:', err);
        this.showToastMessage('❌ Błąd: ' + err.message, 'error');
      }
    },
  },
  computed: {
    filteredAndSortedReminders() {
      let filtered = this.serviceReminders;

      // Filtrowanie po wyszukiwaniu
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(reminder => {
          const contractorName = (reminder.contractor.label || reminder.contractor.name || '').toLowerCase();
          const contractorEmail = (reminder.contractor.email || '').toLowerCase();
          const address = (reminder.address || '').toLowerCase();
          const binder = (reminder.binder || '').toLowerCase();
          
          return contractorName.includes(query) ||
                 contractorEmail.includes(query) ||
                 address.includes(query) ||
                 binder.includes(query);
        });
      }

      // Sortowanie
      const sorted = [...filtered];
      sorted.sort((a, b) => {
        let aVal, bVal;

        switch (this.sortColumn) {
          case 'contractor':
            aVal = (a.contractor.label || a.contractor.name || '').toLowerCase();
            bVal = (b.contractor.label || b.contractor.name || '').toLowerCase();
            break;
          case 'address':
            aVal = (a.address || '').toLowerCase();
            bVal = (b.address || '').toLowerCase();
            break;
          case 'binder':
            aVal = (a.binder || '-').toLowerCase();
            bVal = (b.binder || '-').toLowerCase();
            break;
          case 'days':
            aVal = a.daysUntilExpiry || 0;
            bVal = b.daysUntilExpiry || 0;
            break;
          case 'installationDate':
            aVal = a.installationDate ? a.installationDate.getTime() : 0;
            bVal = b.installationDate ? b.installationDate.getTime() : 0;
            break;
          case 'lastService':
            aVal = a.lastServiceDate ? a.lastServiceDate.getTime() : 0;
            bVal = b.lastServiceDate ? b.lastServiceDate.getTime() : 0;
            break;
          case 'plannedService':
            aVal = a.plannedServiceDate ? a.plannedServiceDate.getTime() : 0;
            bVal = b.plannedServiceDate ? b.plannedServiceDate.getTime() : 0;
            break;
          default:
            return 0;
        }

        if (aVal < bVal) return this.sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return this.sortDirection === 'asc' ? 1 : -1;
        return 0;
      });

      // --- Grupowanie (Union-Find) ---
      const parent = {};
      sorted.forEach(r => { parent[r.id] = r.id; });
      const findRoot = x => { while (parent[x] !== x) x = parent[x] = parent[parent[x]]; return x; };
      const unionIds = (a, b) => { const ra = findRoot(a), rb = findRoot(b); if (ra !== rb) parent[ra] = rb; };

      const byContractor = new Map();
      const byAddress = new Map();
      const byPhone = new Map();
      const byEmail = new Map();
      sorted.forEach(r => {
        const cid = r.contractor && r.contractor.id;
        if (cid) {
          if (!byContractor.has(cid)) byContractor.set(cid, []);
          byContractor.get(cid).push(r.id);
        }
        const addr = (r.address || '').trim().toLowerCase().replace(/\s+/g, ' ');
        if (addr && addr !== '-') {
          if (!byAddress.has(addr)) byAddress.set(addr, []);
          byAddress.get(addr).push(r.id);
        }
        const phone = ((r.contractor && (r.contractor.phone || r.contractor.phoneNumber)) || '').replace(/\D/g, '');
        if (phone.length >= 7) {
          if (!byPhone.has(phone)) byPhone.set(phone, []);
          byPhone.get(phone).push(r.id);
        }
        const email = ((r.contractor && r.contractor.email) || '').trim().toLowerCase();
        if (email && email.includes('@')) {
          if (!byEmail.has(email)) byEmail.set(email, []);
          byEmail.get(email).push(r.id);
        }
      });
      for (const ids of byContractor.values()) for (let i = 1; i < ids.length; i++) unionIds(ids[0], ids[i]);
      for (const ids of byAddress.values()) for (let i = 1; i < ids.length; i++) unionIds(ids[0], ids[i]);
      for (const ids of byPhone.values()) for (let i = 1; i < ids.length; i++) unionIds(ids[0], ids[i]);
      for (const ids of byEmail.values()) for (let i = 1; i < ids.length; i++) unionIds(ids[0], ids[i]);

      const groupCount = {};
      sorted.forEach(r => { const root = findRoot(r.id); groupCount[root] = (groupCount[root] || 0) + 1; });

      const grouped = new Map();
      sorted.forEach(r => {
        const root = findRoot(r.id);
        if (groupCount[root] > 1) {
          if (!grouped.has(root)) grouped.set(root, []);
          grouped.get(root).push(r);
        }
      });

      // Zgrupowane wiersze obok siebie
      const result = [];
      const seen = new Set();
      for (const r of sorted) {
        if (seen.has(r.id)) continue;
        const root = findRoot(r.id);
        if (groupCount[root] > 1) {
          for (const member of grouped.get(root)) {
            if (!seen.has(member.id)) {
              seen.add(member.id);
              result.push({ ...member, _groupId: root });
            }
          }
        } else {
          seen.add(r.id);
          result.push({ ...r, _groupId: null });
        }
      }
      return result;
    },
    groupMetaMap() {
      const rows = this.filteredAndSortedReminders;
      const palette = ['#4a90e2', '#e27340', '#2ecc71', '#9b59b6', '#e74c3c', '#f0a500'];
      const rootColors = new Map();
      let colorIdx = 0;
      const meta = new Map();
      for (let i = 0; i < rows.length; i++) {
        const gid = rows[i]._groupId;
        if (!gid) { meta.set(rows[i].id, { grouped: false }); continue; }
        if (!rootColors.has(gid)) { rootColors.set(gid, palette[colorIdx % palette.length]); colorIdx++; }
        const color = rootColors.get(gid);
        const prevSame = i > 0 && rows[i - 1]._groupId === gid;
        const nextSame = i < rows.length - 1 && rows[i + 1]._groupId === gid;
        let position;
        if (!prevSame && nextSame) position = 'first';
        else if (prevSame && nextSame) position = 'middle';
        else if (prevSame && !nextSame) position = 'last';
        else position = 'only';
        meta.set(rows[i].id, { grouped: true, position, color });
      }
      return meta;
    },
    contractorInstallationsMap() {
      const map = new Map();
      for (const item of this.warrantyInstallationsCache) {
        const cid = item.data && item.data.contractor && item.data.contractor.id;
        if (!cid) continue;
        if (!map.has(cid)) map.set(cid, []);
        map.get(cid).push(item);
      }
      return map;
    },
    nearbyInstallationsMap() {
      const inWindowIds = new Set(this.serviceReminders.map(r => r.id));
      const result = new Map();
      for (const reminder of this.serviceReminders) {
        const contractorId = reminder.contractor && reminder.contractor.id;
        if (!contractorId) { result.set(reminder.id, []); continue; }
        const all = this.contractorInstallationsMap.get(contractorId) || [];
        result.set(reminder.id, all
          .filter(i => !inWindowIds.has(i.id))
          .map(i => ({
            id: i.id,
            address: (i.data.contractor && i.data.contractor.address) || i.data.address || '-',
            binder: i.data.binder || '-',
            plannedService: i.data.plannedService || null,
            lastService: i.data.service || null,
          }))
        );
      }
      return result;
    }
  },
  mounted() {
    this.subscribeWarrantyInstallations();
  },
  beforeDestroy() {
    if (this.unsubscribeWarrantyInstallations) {
      this.unsubscribeWarrantyInstallations();
      this.unsubscribeWarrantyInstallations = null;
    }
  }
};
</script>

<style scoped>
.warranty-service-view {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  min-height: 100vh;
  background: #f8f9fa;
}

/* Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 40px;
  padding: 25px 30px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 15px;
  color: white;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.header-content h1 {
  margin: 0 0 8px 0;
  font-size: 32px;
  font-weight: 700;
}

.page-subtitle {
  margin: 0;
  font-size: 15px;
  opacity: 0.9;
}

.btn-back {
  padding: 10px 20px;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
  cursor: pointer;
  font-size: 14px;
}

.btn-back:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: white;
}

/* Container */
.warranty-container {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

/* Stats */
.stats-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

.stat-card {
  background: white;
  padding: 25px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  display: flex;
  gap: 20px;
  align-items: flex-start;
  border-top: 4px solid #667eea;
  transition: all 0.3s ease;
}

.stat-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.stat-icon {
  font-size: 32px;
  min-width: 50px;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #667eea;
  margin: 0;
}

.stat-label {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin: 4px 0;
}

.stat-description {
  font-size: 12px;
  color: #999;
  margin: 0;
}

/* Action Section */
.action-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.action-card {
  background: white;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.action-card.full-width {
  grid-column: 1 / -1;
}

.action-header {
  margin-bottom: 20px;
}

.action-title {
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 700;
  color: #333;
}

.action-description {
  margin: 0;
  font-size: 14px;
  color: #666;
}

.action-content {
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e9ecef;
}

/* Communication Channels */
.communication-channels {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
}

.channel-checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 8px 16px;
  background: white;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.channel-checkbox:hover {
  border-color: #667eea;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1);
}

.channel-checkbox input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.channel-label {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  user-select: none;
}

.channel-checkbox input:checked + .channel-label {
  color: #667eea;
}

.anniversary-stats-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 25px;
}

.anniversary-stats-section .stat-card {
  border-top: 4px solid #667eea;
}

.anniversary-counter {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px;
  border-radius: 12px;
  text-align: center;
  min-width: 100px;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
}

.anniversary-counter.counter-sent {
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  box-shadow: 0 4px 12px rgba(72, 187, 120, 0.2);
}

.counter-value {
  font-size: 32px;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 6px;
}

.counter-label {
  font-size: 12px;
  font-weight: 600;
  opacity: 0.9;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.anniversary-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-bottom: 20px;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.btn-icon.spinner {
  display: inline-block;
  animation: spin 1s linear infinite;
}

.btn-send-warranty {
  padding: 14px 28px;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-send-warranty:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

.btn-send-warranty:active:not(:disabled) {
  transform: translateY(0);
}

.btn-send-warranty:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.btn-icon {
  font-size: 18px;
  line-height: 1;
}

/* Warranty Result */
.warranty-result {
  padding: 16px;
  border-radius: 8px;
  border-left: 4px solid;
}

.result-success {
  background: #d4edda;
  border-left-color: #28a745;
}

.result-error {
  background: #f8d7da;
  border-left-color: #dc3545;
}

.result-header {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 10px;
}

.result-icon {
  font-size: 20px;
  font-weight: 700;
}

.result-header h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
}

.result-success .result-header h3 {
  color: #155724;
}

.result-error .result-header h3 {
  color: #721c24;
}

.result-details {
  font-size: 13px;
  margin: 0;
}

.result-success .result-details {
  color: #155724;
}

.error-message {
  margin: 0;
  font-size: 13px;
  color: #721c24;
}

/* Info Section */
.info-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
  grid-column: 1 / -1;
}

.info-card {
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.info-card h3 {
  margin: 0 0 15px 0;
  font-size: 15px;
  font-weight: 700;
  color: #333;
}

.info-list {
  margin: 0;
  padding-left: 20px;
  list-style: none;
}

.info-list li {
  margin: 8px 0;
  color: #666;
  font-size: 14px;
  line-height: 1.6;
}

.faq-item {
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e9ecef;
}

.faq-item:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.faq-item p {
  margin: 6px 0;
  font-size: 13px;
  line-height: 1.6;
}

.faq-item p:first-child {
  color: #333;
  font-weight: 600;
}

.faq-item p:last-child {
  color: #666;
}

/* Send Modes */
.send-modes {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-bottom: 20px;
}

.btn-send-all {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.btn-send-selected {
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
}

.btn-send-selected:disabled {
  background: #ccc;
  cursor: not-allowed;
}

/* Installations List */
.installations-list {
  margin-top: 20px;
}

.no-installations {
  text-align: center;
  padding: 40px;
  color: #999;
}

/* Search Section */
.search-section {
  position: relative;
  margin-bottom: 20px;
}

.search-input {
  width: 100%;
  padding: 12px 40px 12px 16px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
}

.search-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.search-clear {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  color: #999;
  font-size: 18px;
  padding: 4px 8px;
  transition: color 0.2s ease;
}

.search-clear:hover {
  color: #333;
}

.th-days, .th-status, .th-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}

/* Sortable Headers */
.sortable {
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.2s ease;
}

.sortable:hover {
  background: #e9ecef;
  border-radius: 4px;
  padding: 2px 6px;
  margin: -2px -6px;
}

.sort-icon {
  font-size: 11px;
  color: #667eea;
  font-weight: 700;
}

.installations-table {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
}

.table-header {
  display: grid;
  grid-template-columns: 50px 2fr 1fr 1fr 1fr 1fr 80px;
  gap: 12px;
  background: #f8f9fa;
  padding: 12px 15px;
  font-weight: 600;
  font-size: 13px;
  color: #555;
  border-bottom: 2px solid #e9ecef;
  align-items: center;
}

/* Unified Service Table - nowa tabela z wszystkimi kolumnami */
.unified-service-table .table-header {
  grid-template-columns: 50px 2.5fr 2fr 1fr 1.2fr 1.2fr 1.2fr 1fr 100px 90px 100px;
}

.unified-service-table .table-row {
  grid-template-columns: 50px 2.5fr 2fr 1fr 1.2fr 1.2fr 1.2fr 1fr 100px 90px 100px;
  padding: 12px 15px;
}

.service-anniversaries-table .table-header {
  grid-template-columns: 40px 2fr 1fr 1fr 1fr 100px 80px;
}

.table-row {
  display: grid;
  grid-template-columns: 50px 2fr 1fr 1fr 1fr 1fr 80px;
  gap: 12px;
  padding: 12px 15px;
  border-bottom: 1px solid #e9ecef;
  transition: all 0.2s ease;
  align-items: stretch;
}

.service-anniversaries-table .table-row {
  grid-template-columns: 40px 2fr 1fr 1fr 1fr 100px 80px;
}

.table-row:hover {
  background: #f8f9fa;
}

.table-row.expired {
  background: #fff3cd;
}

.table-row.selected {
  background: #d4edda;
}

.table-row:last-child {
  border-bottom: none;
}

.td-checkbox input,
.th-checkbox input {
  cursor: pointer;
  width: 18px;
  height: 18px;
}

.td-contractor {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.td-contractor strong {
  font-size: 14px;
  color: #333;
}

.td-contractor small {
  font-size: 12px;
  color: #999;
}

.td-date, .td-reminder, .td-address, .td-binder,
.td-installation-date, .td-last-service, .td-planned-service {
  font-size: 13px;
  color: #666;
  display: flex;
  align-items: center;
}

.td-days {
  display: flex;
  align-items: center;
  justify-content: center;
}

.td-status {
  display: flex;
  align-items: center;
  justify-content: center;
}

.days-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  min-width: 80px;
  text-align: center;
}

.badge-expired {
  background: #dc3545;
  color: white;
}

.badge-urgent {
  background: #ffc107;
  color: #333;
}

.badge-soon {
  background: #17a2b8;
  color: white;
}

.badge-normal {
  background: #28a745;
  color: white;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  min-width: 85px;
  text-align: center;
}

.status-warranty-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.btn-resend {
  background: #667eea;
  color: white;
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
  padding: 0;
}

.btn-resend:hover {
  background: #5568d3;
  transform: rotate(180deg);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.btn-resend:active {
  transform: rotate(180deg) scale(0.95);
}

/* Action buttons in tables */
.th-actions {
  min-width: 100px;
  text-align: center;
}

.td-actions {
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
}

.btn-action-details,
.btn-action {
  background: white;
  color: #667eea;
  border: 2px solid #667eea;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  white-space: nowrap;
  font-weight: 600;
}

.btn-action-details {
  border-color: #667eea;
  color: #667eea;
}

.btn-action-details:hover {
  background: #667eea;
  color: white;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
  transform: translateY(-1px);
}

.btn-action {
  background: #667eea;
  color: white;
  border: none;
  padding: 6px 10px;
}

.btn-action:hover:not(:disabled) {
  background: #5568d3;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
  transform: translateY(-1px);
}

.btn-action:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-send-service {
  background: #667eea;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  white-space: nowrap;
}

.btn-send-service:hover:not(:disabled) {
  background: #5568d3;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
  transform: translateY(-1px);
}

.btn-send-service:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.status-sent {
  background: #d4edda;
  color: #155724;
}

.status-pending {
  background: #fff3cd;
  color: #856404;
}

.status-warranty {
  background: #cce5ff;
  color: #004085;
  font-weight: 600;
}

/* Toast */
.success-toast {
  position: fixed;
  top: 20px;
  right: 20px;
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  color: white;
  padding: 16px 24px;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(72, 187, 120, 0.4);
  font-size: 15px;
  font-weight: 600;
  z-index: 1000;
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    transform: translateX(400px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

/* Responsywność */
@media (max-width: 768px) {
  .warranty-service-view {
    padding: 12px;
  }

  .page-header {
    flex-direction: column;
    gap: 15px;
    padding: 20px;
  }

  .page-header h1 {
    font-size: 24px;
  }

  .action-section {
    grid-template-columns: 1fr;
  }

  .action-card {
    padding: 20px;
  }

  .stats-section {
    grid-template-columns: 1fr;
  }

  .stat-card {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .btn-send-warranty {
    width: 100%;
  }

  .send-modes {
    grid-template-columns: 1fr;
  }

  .table-header,
  .table-row,
  .unified-service-table .table-header,
  .unified-service-table .table-row {
    grid-template-columns: 40px 1fr;
    font-size: 12px;
  }

  .th-date, .th-reminder, .th-days, .th-status, .th-address, .th-binder,
  .th-installation-date, .th-last-service, .th-planned-service, .th-nearby,
  .td-date, .td-reminder, .td-days, .td-status, .td-address, .td-binder,
  .td-installation-date, .td-last-service, .td-planned-service, .td-nearby {
    display: none;
  }

  .td-contractor small {
    font-size: 11px;
  }
}

/* ── Przy okazji ── */
.th-nearby {
  font-size: 12px;
  color: #6c757d;
  text-align: center;
}

.td-nearby {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-nearby {
  background: #e8f4fd;
  color: #2980b9;
  border: 1px solid #bee3f8;
  border-radius: 12px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s ease;
}

.btn-nearby:hover {
  background: #bee3f8;
}

.nearby-none {
  color: #ccc;
  font-size: 13px;
}

.nearby-popover {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 500;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(0,0,0,0.15);
  min-width: 280px;
  max-width: 360px;
}

.nearby-popover-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  border-radius: 8px 8px 0 0;
  font-size: 12px;
  font-weight: 600;
  color: #495057;
}

.nearby-popover-close {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  color: #6c757d;
  padding: 0 4px;
  line-height: 1;
}

.nearby-popover-close:hover {
  color: #333;
}

.nearby-popover-item {
  padding: 10px 14px;
  border-bottom: 1px solid #f1f3f5;
  cursor: pointer;
  transition: background 0.15s ease;
}

.nearby-popover-item:last-child {
  border-bottom: none;
  border-radius: 0 0 8px 8px;
}

.nearby-popover-item:hover {
  background: #f0f7ff;
}

.nearby-item-address {
  font-size: 13px;
  font-weight: 500;
  color: #333;
  margin-bottom: 3px;
}

.nearby-item-meta {
  display: flex;
  gap: 10px;
  font-size: 11px;
  color: #6c757d;
}

/* ── Grupowanie wierszy ── */
.table-row.group-row {
  border-left: 3px solid var(--group-color, #4a90e2) !important;
  border-right: 1px solid var(--group-color, #4a90e2) !important;
  background-color: #f5f8ff;
}

.table-row.group-first {
  border-top: 2px solid var(--group-color, #4a90e2) !important;
  border-bottom: none !important;
  margin-top: 8px;
}

.table-row.group-middle {
  border-bottom: none !important;
}

.table-row.group-last {
  border-bottom: 2px solid var(--group-color, #4a90e2) !important;
  margin-bottom: 8px;
}

.table-row.group-only {
  border: 2px solid var(--group-color, #4a90e2) !important;
  margin: 4px 0;
}
</style>

